import { IMessage } from '@zaplify/campaigns';
import { BaseSdk } from '../../base.sdk';
import { ChannelType } from '@zaplify/channel-accounts-shared';
import {
    MessageType,
    MessageDto,
    GenerateMessageForLinkedinExtensionResult,
    MessagePathsForLinkedinExtensionResult,
    MessageGenLinkedinExtensionCountsResult,
} from '@zaplify/services-messaging-shared';
import { MessagePathsForLinkedinExtensionMessage } from '@zaplify/services-messaging-shared';
export class CampaignsSdk extends BaseSdk {
    // @TODO move to messenger sdk
    async setupMessenger(language?: string) {
        return this.post<{ playbookId: string; assistantSettingsId: string }>({
            path: `messenger/setup`,
            payload: language ? { groupSettings: { languages: [language] } } : {},
        });
    }

    async sendSuggestedEmailMessage(
        messageId: string,
        prospectId: string,
        subject: string,
        content: string,
    ): Promise<MessageDto> {
        return this.put(`${this.campaignsPath}/email/message/${messageId}`, {
            prospectId,
            subject,
            content,
        });
    }

    async sendNewEmailMessage({
        prospectId,
        subject,
        content,
    }: {
        prospectId: string;
        subject: string;
        content: string;
    }): Promise<MessageDto> {
        return this.post({
            path: `${this.campaignsPath}/email/message`,
            payload: {
                prospectId,
                subject,
                content,
            },
        });
    }

    /** Feedback must be non-empty string. Changes the subjectLine and content of message in DB, and returns the altered entity. */
    async regenerateMessage({
        messageId,
        feedback,
        content,
        subjectLine,
        actionType,
    }: {
        messageId: string;
        feedback: string;
        content?: string;
        subjectLine?: string;
        actionType: MessageType;
    }): Promise<IMessage> {
        return this.post({
            path: `${this.campaignsPath}/messages/${messageId}/regenerate`,
            payload: {
                feedback,
                content,
                subjectLine,
                actionType,
            },
        });
    }

    async generateMessageFromPath({
        messageId,
        path,
        prospectId,
        actionType,
    }: {
        messageId?: string;
        path: string;
        prospectId: string;
        actionType: MessageType;
    }): Promise<{ content: string; subjectLine?: string }> {
        return this.post({
            path: `${this.campaignsPath}/messages/generate-from-path`,
            payload: {
                path,
                messageId,
                prospectId,
                actionType,
            },
        });
    }

    /** Feedback must be non-empty string. Changes the subjectLine and content of message in DB, and returns the altered entity. */
    async generateMessageAsEmail({
        messageId,
        content,
        subjectLine,
    }: {
        messageId: string;
        content?: string;
        subjectLine?: string;
    }): Promise<IMessage> {
        return this.post({
            path: `${this.campaignsPath}/messages/${messageId}/as-email`,
            payload: {
                content,
                subjectLine,
            },
        });
    }

    /** Feedback must be non-empty string. Does not alter values in DB. */
    async getRegeneratedMessage({
        prospectId,
        channelType,
        actionType,
        feedback,
        message,
    }: {
        prospectId: string;
        channelType?: ChannelType;
        actionType: MessageType;
        feedback: string;
        message: { subjectLine?: string; content: string };
    }): Promise<{ subjectLine?: string; content: string } | null> {
        const queryParams = new URLSearchParams({
            prospectId,
            actionType,
            feedback,
            ...message,
        });
        if (channelType) queryParams.append('channelType', channelType);

        return this.get<{ subjectLine?: string; content: string }>(
            `${this.campaignsPath}/messages/regenerated?${queryParams.toString()}`,
        );
    }

    async setLinkedinMessageSuggestionSent({
        prospectId,
        content,
        sentOn,
        messageId,
        externalMessageId,
        conversationId,
        messageType,
        linkedinAccountId,
    }: {
        prospectId: string;
        content: string;
        sentOn: Date;
        messageId: string | undefined;
        externalMessageId: string;
        conversationId: string | null;
        messageType: MessageType.linkedinConnectionRequest | MessageType.linkedinMessage;
        linkedinAccountId: string;
    }) {
        return this.put(`${this.campaignsPath}/linkedin/message/${messageId}`, {
            prospectId,
            content,
            sentOn,
            messageType,
            externalMessageId,
            conversationId,
            linkedinAccountId,
        });
    }

    async getMessagePathsForLinkedinExtension({
        messages,
    }: {
        prospectId?: string;
        messages: MessagePathsForLinkedinExtensionMessage[];
    }): Promise<MessagePathsForLinkedinExtensionResult> {
        return this.post({
            path: `${this.campaignsPath}/messages/generate-message-paths-for-linkedin-extension`,
            payload: {
                messages,
            },
        });
    }

    async generateMessageForLinkedinExtension({
        messages,
        selectedIntent,
        customInstructions,
    }: {
        messages: MessagePathsForLinkedinExtensionMessage[];
        selectedIntent: string;
        customInstructions?: string;
    }): Promise<GenerateMessageForLinkedinExtensionResult> {
        return this.post({
            path: `${this.campaignsPath}/messages/generate-message-for-linkedin-extension`,
            payload: {
                messages,
                selectedIntent,
                customInstructions,
            },
        });
    }

    async getMessageGenLinkedinExtensionCounts(): Promise<MessageGenLinkedinExtensionCountsResult> {
        return this.get(
            `${this.campaignsPath}/messages/generate-linkedin-extension-counts`,
        ) as Promise<MessageGenLinkedinExtensionCountsResult>;
    }
}
